import {
  Chips,
  column,
  defaultPage,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  Icon,
  multiFilter,
  PageHeaderLayout,
  Palette,
  RowStyles,
  selectColumn,
  simpleDataRows,
  singleFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { TimeFrequency, TradePartnerFilter, TradePartnerOnboardingStatus } from "src/generated/graphqlTypes";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { iconFilter } from "src/routes/trade-partners/components/IconFilter";
import {
  TradePartnersMetadataQuery,
  TradePartnersPage_TradeCategoriesFragment,
  TradePartnersPageQuery,
  TradePartnersPageTradePartnerFragment,
  useTradePartnersMetadataQuery,
  useTradePartnersPageQuery,
} from "src/routes/trade-partners/TradePartnersPage.generated";
import { createExportCsvUrl, createTradePartnerUrl } from "src/RouteUrls";
import { paymentTermsRoute, tradeCategoryLeadTimesRoute } from "src/routing/domains/tradePartnersPaths";
import { queryResult } from "src/utils/queryResult";
import { openInSelf } from "src/utils/window";
import { formatLeadTime } from "./trade-category-lead-times/utils";

export function TradePartnersPage() {
  const metadataQuery = useTradePartnersMetadataQuery({ fetchPolicy: "cache-first" });
  useDocumentTitle("Trade Partners");
  return queryResult(metadataQuery, {
    data: ({ markets, tradeCategories, developments, cohorts }) => (
      <TradePartnersDataView
        markets={markets}
        tradeCategories={tradeCategories}
        developments={developments}
        cohorts={cohorts}
      />
    ),
  });
}

function TradePartnersDataView({
  markets: allMarkets,
  tradeCategories: allTradeCategories,
  developments: allDevelopments,
  cohorts: allCohorts,
}: TradePartnersMetadataQuery) {
  const tableApi = useGridTableApi<Row>();

  const filterDefs: FilterDefs<PageFilter> = useMemo(() => {
    const markets = multiFilter({ options: allMarkets, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });

    const developmentIds = multiFilter({
      options: allDevelopments,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const cohortIds = multiFilter({ options: allCohorts, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });

    const onboardingStatus = multiFilter({
      label: "Onboarding",
      options: [
        { id: TradePartnerOnboardingStatus.InProgress, name: "In Progress" },
        { id: TradePartnerOnboardingStatus.Complete, name: "Complete" },
        { id: TradePartnerOnboardingStatus.DoNotUse, name: "Do Not Use" },
        { id: TradePartnerOnboardingStatus.Inactive, name: "Inactive" },
      ],
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const isSweetheart = iconFilter({ label: "Favorite", defaultValue: false });

    const averageScore = singleFilter({
      label: "Average Rating",
      options: [
        { id: "1", name: ratingLabel(1) },
        { id: "2", name: ratingLabel(2) },
        { id: "3", name: ratingLabel(3) },
        { id: "4", name: ratingLabel(4) },
      ],
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const tradeCategories = multiFilter({
      options: allTradeCategories,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    });

    return { markets, onboardingStatus, averageScore, isSweetheart, tradeCategories, developmentIds, cohortIds };
  }, [allMarkets, allTradeCategories, allDevelopments, allCohorts]);

  const layoutState = useGridTableLayoutState<PageFilter>({
    persistedFilter: { filterDefs, storageKey: "tradePartnerFilter" },
    search: "server",
  });

  const selectedTradePartners = useComputed(() => tableApi.getSelectedRowIds("data"), [tableApi]);
  const tableActions: HeaderAction[] = useMemo(
    () => [
      {
        kind: "menu",
        trigger: { icon: "verticalDots", variant: "outline" },
        tooltip: "Actions",
        items: [
          {
            label: "Export Contacts to CSV",
            disabled: !selectedTradePartners.length,
            onClick: () =>
              openInSelf(`${createExportCsvUrl("tradePartnersContacts")}&tpIds=${selectedTradePartners.join()}`),
          },
        ],
      },
    ],
    [selectedTradePartners],
  );

  const query = useTradePartnersPageQuery({
    variables: {
      filter: {
        ...layoutState.filter,
        // If no onboarding status is selected, fetch all statuses including `DoNotUse` in the trades page
        // We do this manually client side because the tradePartners resolver is reused various places where we don't want to include `DoNotUse`
        // TODO: Add a BE filter `includeDoNotUse` to the tradePartners resolver that we can pass to all tradePartners queries to include `DoNotUse` status in a fetch all or query by id
        ...(!layoutState.filter.onboardingStatus && { onboardingStatus: Object.values(TradePartnerOnboardingStatus) }),
        search: layoutState.searchString || undefined,
        averageScore: { gte: parseInt(layoutState.filter.averageScore || "0") },
      },
      page: defaultPage,
    },
  });

  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.tradePartnersPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.tradePartnersPage.entities?.length, limit: defaultPage.limit } },
      });
    }
  }, [query]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Trade Partners",
        actions: [
          { label: "Manage Lead Times", variant: "secondary", onClick: tradeCategoryLeadTimesRoute.url() },
          { label: "Manage Payment Terms", variant: "secondary", onClick: paymentTermsRoute.url() },
          { label: "Create", variant: "primary", onClick: createTradePartnerUrl() },
        ],
      }}
    >
      <GridTableLayout
        layoutState={layoutState}
        actions={tableActions}
        tableProps={{
          api: tableApi,
          query,
          createRows,
          columns,
          rowStyles: createRowStyles(),
          as: "virtual",
          emptyFallback: "There are no Trade Partners",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: TradePartnersPageTradePartnerFragment };
type Row = HeaderRow | DataRow;

const columns: GridColumn<Row>[] = [
  selectColumn<Row>({ name: "Select" }),
  column<Row>({ id: "name", name: "Trade Partner", header: "Trade Partner", data: (tp) => tp.name, mw: "200px" }),
  column<Row>({
    id: "isSweetheart",
    name: "Favorite",
    header: emptyCell,
    data: (tp) => (tp.isSweetheart ? <Icon icon="star" color={Palette.Gray700} /> : emptyCell),
    w: "48px",
    mw: "48px",
  }),
  column<Row>({
    id: "onboardingStatus",
    name: "Onboarding Status",
    header: "Onboarding Status",
    data: (tp) => tp.onboardingStatus.name,
    mw: "140px",
  }),
  column<Row>({
    id: "averageScore",
    name: "Average Rating",
    header: "Average Rating",
    data: (tp) => tp.averageScore,
    mw: "120px",
  }),
  column<Row>({
    id: "markets",
    name: "Markets",
    header: "Markets",
    data: (tp) => tp.markets.map((m) => m.name).join(", "),
    mw: "160px",
  }),
  column<Row>({
    id: "tradeCategories",
    name: "Trade Categories",
    header: "Trade Categories",
    data: (tp) => (
      <div>
        <Chips values={tp.tradeCategories.map((tc) => tc.name)} />
      </div>
    ),
    mw: "180px",
  }),
  column<Row>({
    id: "leadTime",
    name: "Lead Time",
    header: "Lead Time",
    data: (tp) => getLongestTradeCategoryLeadTime(tp.tradeCategories, tp),
    mw: "100px",
  }),
];

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: (row) => createTradePartnerUrl(row.data.id) } };
}

function createRows(data: TradePartnersPageQuery | undefined): GridDataRow<Row>[] {
  return simpleDataRows(data?.tradePartnersPage.entities);
}

type PageFilter = Omit<TradePartnerFilter, "averageScore"> & { averageScore: string };

function ratingLabel(numStars: number) {
  const total = 5;
  const numEmpty = total - numStars;
  const starb = "\u2605"; // black
  const starw = "\u2606"; // white

  return `${starb.repeat(numStars)}${starw.repeat(numEmpty)} & up`;
}

// helper function to grab the longest trade category lead times
function getLongestTradeCategoryLeadTime(
  tradeCategories: TradePartnersPage_TradeCategoriesFragment[],
  tradeParter: TradePartnersPageTradePartnerFragment,
) {
  const tradePartnerMarketIds = tradeParter.markets.map((m) => m.id);
  // find the trade category with the longest lead time
  const longestLeadTime = tradeCategories
    .flatMap((tc) => tc.leadTimes)
    .filter((lt) => tradePartnerMarketIds.includes(lt.market.id))
    .sort((a, b) => b.leadTimeInDays - a.leadTimeInDays)[0];

  // if we don't have a lead time from our trade categories, then use our default lead time config
  if (!longestLeadTime) {
    return formatLeadTime(defaultLeadTime.leadTimeInDays, defaultLeadTime.leadTimeFrequency);
  }
  return formatLeadTime(longestLeadTime.leadTimeInDays, longestLeadTime.leadTimeFrequency);
}

// creating a default config to stay in sync with graphql service (tradeCategoryLeadTimesResolver - line 28)
const defaultLeadTime = { leadTimeInDays: 14, leadTimeFrequency: { code: TimeFrequency.Weeks, name: "Weeks" } };
